import { Analytics } from '@cobliteam/analytics';
import { useEffect, useRef, useState } from 'react';
import type { NudgeKind, NudgeSurface } from './types';

export interface UseNudgeOptions {
  id: string;
  kind: NudgeKind;
  surface: NudgeSurface;
  /** Hook deciding eligibility — feature flags, count thresholds, permissions. */
  useShouldShow: () => boolean;
}

export interface UseNudgeResult {
  shouldShow: boolean;
  dismiss: (reason?: string) => void;
  accept: (metadata?: Record<string, unknown>) => void;
  trackClick: (metadata?: Record<string, unknown>) => void;
}

/**
 * Dismiss is scoped by surface on purpose: closing the groups nudge in the floating card leaves
 * the groups page banner alone.
 */
const dismissKey = (id: string, surface: NudgeSurface) => `nudge:${id}:${surface}:dismissed`;

/** Já dispensado nesta sessão? Exportado para a fonte do nudge não buscar dados que não vai exibir. */
export const readDismissed = (id: string, surface: NudgeSurface): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(dismissKey(id, surface)) === 'true';
  } catch {
    return false;
  }
};

const persistDismissed = (id: string, surface: NudgeSurface) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(dismissKey(id, surface), 'true');
  } catch {
    // sessionStorage unavailable (private mode, quota exceeded) — fall back to in-memory only;
    // the nudge reappears on next mount. Deliberately NOT localStorage: dismissals are not meant
    // to survive the session.
  }
};

/**
 * Lifecycle + analytics for a single nudge. Generalizes `useSuggestion` (which this replaces) by
 * adding `kind` and `surface`; the event NAMES are unchanged so the existing Mixpanel history
 * stays continuous.
 */
export const useNudge = ({ id, kind, surface, useShouldShow }: UseNudgeOptions): UseNudgeResult => {
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed(id, surface));
  const shouldShow = useShouldShow() && !dismissed;

  // Re-sync whenever the identity changes so a hook reused with a different id/surface does not
  // leak the previous one's dismiss state.
  useEffect(() => {
    setDismissed(readDismissed(id, surface));
  }, [id, surface]);

  /*
   * Once per mount, not once per false → true flip.
   *
   * Nudges live in the app shell and never unmount, and some gate on the route (a recommendation is
   * suppressed on its own page). Navigating there and back flips `shouldShow` true → false → true,
   * which would re-emit the event and turn the metric into a navigation count. A ref on the hook
   * instance is the right scope: it lasts exactly as long as the nudge is on screen.
   */
  const hasReportedDisplay = useRef(false);

  useEffect(() => {
    if (shouldShow && !hasReportedDisplay.current) {
      hasReportedDisplay.current = true;
      Analytics.track('Suggestion Displayed', { suggestionId: id, kind, surface });
    }
  }, [shouldShow, id, kind, surface]);

  return {
    shouldShow,
    dismiss: (reason) => {
      Analytics.track('Suggestion Dismissed', { suggestionId: id, kind, surface, reason });
      persistDismissed(id, surface);
      setDismissed(true);
    },
    // Metadata is spread FIRST so a caller cannot overwrite the identity fields.
    accept: (metadata) => {
      Analytics.track('Suggestion Accepted', { ...metadata, suggestionId: id, kind, surface });
    },
    trackClick: (metadata) => {
      Analytics.track('Suggestion Clicked', { ...metadata, suggestionId: id, kind, surface });
    },
  };
};

export default useNudge;
